import { useEffect } from 'react';

type OutsideClickRef<T extends HTMLElement> = React.RefObject<T> | React.RefObject<T>[];

export default function useOutsideClick<T extends HTMLElement>(
  ref: OutsideClickRef<T>,
  handler: () => void
) {
  useEffect(() => {
    const refs = Array.isArray(ref) ? ref : [ref];

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const clickedInside = refs.some((entry) => {
        const el = entry.current;
        return !!el && el.contains(target);
      });

      // Ignore if clicking inside any tracked element
      if (clickedInside) return;

      handler();
    };

    // Use capture so components that stop propagation (e.g., sliders) don't block outside clicks.
    document.addEventListener('mousedown', listener, true);
    document.addEventListener('touchstart', listener, true);

    return () => {
      document.removeEventListener('mousedown', listener, true);
      document.removeEventListener('touchstart', listener, true);
    };
  }, [ref, handler]);
}
